// safe-data.js — normalisation défensive des données locales affichées.
//
// IndexedDB peut contenir des lignes provenant d'anciennes versions, d'un
// import d'historique incomplet ou d'une écriture interrompue. Les composants
// React ne doivent jamais rendre directement une valeur non typée issue du
// stockage : un seul objet à la place d'un texte suffit à faire tomber tout
// l'écran (« Objects are not valid as a React child »). Ces fonctions sont
// pures, n'envoient rien au serveur et ne modifient aucun contenu chiffré.

export function isRecord(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export function safeText(value, fallback = '') {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return fallback;
}

export function safePseudo(value) {
  return safeText(value).trim();
}

function finiteNumber(value, fallback = 0) {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function uniqueStrings(values) {
  const out = [];
  const seen = new Set();
  for (const value of asArray(values)) {
    const text = safePseudo(value);
    if (!text || seen.has(text)) continue;
    seen.add(text);
    out.push(text);
  }
  return out;
}

export function normalizeContacts(value) {
  const out = [];
  const seen = new Set();
  for (const raw of asArray(value)) {
    if (!isRecord(raw)) continue;
    const pseudo = safePseudo(raw.pseudo);
    if (!pseudo || seen.has(pseudo)) continue;
    seen.add(pseudo);
    out.push({
      ...raw,
      pseudo,
      publicKey: typeof raw.publicKey === 'string' ? raw.publicKey : '',
    });
  }
  return out;
}

export function normalizeGroup(raw) {
  if (!isRecord(raw)) return null;
  const groupId = safePseudo(raw.groupId);
  if (!groupId) return null;
  const name = safeText(raw.name).trim() || 'Groupe';
  return {
    ...raw,
    groupId,
    name,
    members: uniqueStrings(raw.members),
    createdBy: safePseudo(raw.createdBy),
  };
}

export function normalizeGroups(value) {
  const out = [];
  const seen = new Set();
  for (const raw of asArray(value)) {
    const group = normalizeGroup(raw);
    if (!group || seen.has(group.groupId)) continue;
    seen.add(group.groupId);
    out.push(group);
  }
  return out;
}

export function normalizeBlocked(value) {
  return uniqueStrings(value);
}

function normalizeReactions(value) {
  const out = Object.create(null);
  if (!isRecord(value)) return out;
  for (const [rawEmoji, rawWho] of Object.entries(value)) {
    const emoji = safeText(rawEmoji).trim();
    if (!emoji) continue;
    out[emoji] = uniqueStrings(rawWho);
  }
  return out;
}

export function normalizeMessage(raw) {
  if (!isRecord(raw)) return null;
  const peer = safePseudo(raw.peer);
  if (!peer) return null;

  const msgType = safeText(raw.msgType, 'text') || 'text';
  const status = safeText(raw.status);
  const serverIds = asArray(raw.serverIds).filter((id) => (
    typeof id === 'string' || (typeof id === 'number' && Number.isFinite(id))
  ));

  return {
    ...raw,
    peer,
    from: safePseudo(raw.from),
    msgType,
    text: safeText(raw.text),
    fileName: safeText(raw.fileName),
    mimeType: safeText(raw.mimeType),
    fileSize: Math.max(0, finiteNumber(raw.fileSize, 0)),
    sentAt: finiteNumber(raw.sentAt, 0),
    read: raw.read === true,
    status,
    mid: safeText(raw.mid),
    replyToMid: safeText(raw.replyToMid),
    replyPreview: safeText(raw.replyPreview),
    serverIds,
    sentCount: Math.max(0, finiteNumber(raw.sentCount, 0)),
    deliveredCount: Math.max(0, finiteNumber(raw.deliveredCount, 0)),
    failedCount: Math.max(0, finiteNumber(raw.failedCount, 0)),
    total: Math.max(1, finiteNumber(raw.total, 1)),
    reactions: normalizeReactions(raw.reactions),
    important: raw.important === true,
    deliverAt: Math.max(0, finiteNumber(raw.deliverAt, 0)),
    ephemeralSeconds: Math.max(0, finiteNumber(raw.ephemeralSeconds, 0)),
    expiresAt: Math.max(0, finiteNumber(raw.expiresAt, 0)),
    viewOnce: raw.viewOnce === true,
    mentions: uniqueStrings(raw.mentions).slice(0, 32),
    shoppingList: isRecord(raw.shoppingList) ? {
      title: safeText(raw.shoppingList.title, 'Liste de courses').slice(0, 100),
      items: asArray(raw.shoppingList.items).map((x) => safeText(x).trim()).filter(Boolean).slice(0, 200),
    } : undefined,
    poll: isRecord(raw.poll) ? {
      question: safeText(raw.poll.question),
      options: asArray(raw.poll.options).map((x) => safeText(x)).filter(Boolean).slice(0, 6),
      votes: isRecord(raw.poll.votes) ? Object.fromEntries(Object.entries(raw.poll.votes).map(([k, v]) => [k, uniqueStrings(v)])) : {},
    } : undefined,
  };
}

export function normalizeMessages(value) {
  return asArray(value).map(normalizeMessage).filter(Boolean);
}

export function normalizeConvMeta(value) {
  const out = Object.create(null);
  if (!isRecord(value)) return out;

  for (const [rawKey, rawMeta] of Object.entries(value)) {
    const key = safePseudo(rawKey);
    if (!key || !isRecord(rawMeta)) continue;
    const last = isRecord(rawMeta.last) ? normalizeMessage({ ...rawMeta.last, peer: key }) : null;
    out[key] = {
      last,
      unread: Math.max(0, Math.floor(finiteNumber(rawMeta.unread, 0))),
    };
  }
  return out;
}

export function metaFor(convMeta, key) {
  const safe = normalizeConvMeta(convMeta);
  return Object.prototype.hasOwnProperty.call(safe, key) ? safe[key] : undefined;
}

export function previewOf(meta) {
  if (!isRecord(meta) || !isRecord(meta.last)) return '';
  const m = meta.last;
  if (m.poll) return `📊 ${safeText(m.poll.question).slice(0, 44) || 'Sondage'}`;
  if (m.shoppingList) return `🛒 ${m.shoppingList.items?.length || 0} article(s)`;
  if (m.msgType === 'image') return '🖼 Image';
  if (m.msgType === 'video') return '🎬 Vidéo';
  if (m.msgType === 'audio') return '🎤 Message vocal';
  if (m.msgType === 'document' || m.msgType === 'archive') {
    return `📎 ${safeText(m.fileName).trim() || 'Fichier'}`;
  }
  const text = `${m.important ? '❗ ' : ''}${safeText(m.text)}`;
  return text.length > 48 ? `${text.slice(0, 48)}…` : text;
}

export function buildConversationView(identity, contacts, groups, convMeta) {
  const identityPseudo = safePseudo(identity?.pseudo);
  const safeMeta = normalizeConvMeta(convMeta);
  const items = [];
  const seen = new Set();

  function add(item) {
    if (!item.key || seen.has(item.key)) return;
    seen.add(item.key);
    items.push(item);
  }

  if (identityPseudo) {
    add({ key: identityPseudo, label: 'Notes personnelles', pinned: true, group: false });
  }
  for (const contact of normalizeContacts(contacts)) {
    if (contact.pseudo === identityPseudo) continue;
    add({ key: contact.pseudo, label: contact.pseudo, group: false });
  }
  for (const group of normalizeGroups(groups)) {
    add({
      key: group.groupId,
      label: group.name,
      group: true,
      members: group.members,
    });
  }

  items.sort((a, b) => {
    if (a.pinned) return -1;
    if (b.pinned) return 1;
    const bAt = safeMeta[b.key]?.last?.sentAt || 0;
    const aAt = safeMeta[a.key]?.last?.sentAt || 0;
    return bAt - aAt;
  });

  return { identityPseudo, items, convMeta: safeMeta };
}
